import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useShareTemplateByEmail } from '@/hooks/useSharedTemplate';
import { ApiError } from '@/lib/queryClient';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Mail } from 'lucide-react';
import { useForm } from 'react-hook-form';
import {
  type ShareTemplateEmailFormProps,
  type ShareTemplateEmailFormValues,
  shareTemplateEmailSchema,
} from './ShareTemplateEmailForm.types';

export const ShareTemplateEmailForm = ({ templateId }: ShareTemplateEmailFormProps) => {
  const { toast } = useToast();
  const { mutate, isPending } = useShareTemplateByEmail();
  const form = useForm<ShareTemplateEmailFormValues>({
    resolver: zodResolver(shareTemplateEmailSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = ({ email }: ShareTemplateEmailFormValues) =>
    mutate(
      { templateId, email },
      {
        onSuccess: () => {
          toast({ title: 'Tölvupóstur sendur' });
          form.reset();
        },
        onError: (error) =>
          toast({
            title: 'Villa',
            description:
              error instanceof ApiError && error.status === 429
                ? 'Of margar sendingar. Reyndu aftur síðar.'
                : 'Ekki tókst að senda tölvupóst',
            variant: 'destructive',
          }),
      }
    );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2" noValidate>
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Netfang viðtakanda</FormLabel>
              <div className="flex gap-2">
                <FormControl>
                  <Input {...field} type="email" autoComplete="email" placeholder="netfang@daemi.is" />
                </FormControl>
                <Button type="submit" variant="outline" disabled={isPending}>
                  {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
                  Senda tengil
                </Button>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
};
